import React from "react";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";

export const NOTE_AI_EDIT_PRESETS = [
    {
        id: "translate",
        label: "번역하기",
        prompt: "선택 영역을 문서 맥락에 맞게 자연스럽게 번역해줘.",
    },
    {
        id: "proofread",
        label: "교정하기",
        prompt: "선택 영역의 맞춤법, 문장 부호, 어색한 표현을 교정해줘.",
    },
    {
        id: "rewrite",
        label: "다시쓰기",
        prompt: "선택 영역의 의미를 유지하면서 더 자연스럽고 명확한 학술 문체로 다시 써줘.",
    },
    {
        id: "expand",
        label: "늘여쓰기",
        prompt: "선택 영역의 핵심 의미를 유지하면서 설명과 연결 문장을 보강해줘. 없는 사실은 만들지 마.",
    },
    {
        id: "shorten",
        label: "줄여쓰기",
        prompt: "선택 영역의 핵심 의미를 유지하면서 더 간결하게 줄여줘.",
    },
    {
        id: "continue",
        label: "이어쓰기",
        prompt: "선택 영역 뒤에 자연스럽게 이어질 문장을 작성해줘. 문서의 기존 문체와 맥락을 유지해줘.",
    },
];

function tokenizeText(text) {
    return String(text || "").match(/\s+|[^\s]+/g) || [];
}

function buildInlineDiffSegments(previousText, nextText) {
    const leftTokens = tokenizeText(previousText);
    const rightTokens = tokenizeText(nextText);
    const table = Array.from({ length: leftTokens.length + 1 }, () => Array(rightTokens.length + 1).fill(0));
    const segments = [];

    for (let row = leftTokens.length - 1; row >= 0; row -= 1) {
        for (let col = rightTokens.length - 1; col >= 0; col -= 1) {
            table[row][col] =
                leftTokens[row] === rightTokens[col]
                    ? table[row + 1][col + 1] + 1
                    : Math.max(table[row + 1][col], table[row][col + 1]);
        }
    }

    const pushSegment = (type, value) => {
        if (!value) return;
        const last = segments[segments.length - 1];
        if (last?.type === type) {
            last.value += value;
            return;
        }
        segments.push({ type, value });
    };

    let row = 0;
    let col = 0;
    while (row < leftTokens.length && col < rightTokens.length) {
        if (leftTokens[row] === rightTokens[col]) {
            pushSegment("equal", leftTokens[row]);
            row += 1;
            col += 1;
        } else if (table[row + 1][col] >= table[row][col + 1]) {
            pushSegment("remove", leftTokens[row]);
            row += 1;
        } else {
            pushSegment("add", rightTokens[col]);
            col += 1;
        }
    }
    while (row < leftTokens.length) {
        pushSegment("remove", leftTokens[row]);
        row += 1;
    }
    while (col < rightTokens.length) {
        pushSegment("add", rightTokens[col]);
        col += 1;
    }
    return segments;
}

function getDisplayAIEditText(text) {
    return String(text || "").replace(/<locked-node\b[^>]*>([\s\S]*?)<\/locked-node>/gi, "$1");
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background: rgba(15, 23, 42, 0.24);
`;

const Dialog = styled.div`
    display: flex;
    flex-direction: column;
    width: min(880px, calc(100vw - 48px));
    max-height: calc(100vh - 64px);
    border: 1px solid var(--color-Line);
    border-radius: 8px;
    background: var(--color-White);
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.18);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    border-bottom: 1px solid var(--color-Line);
`;

const TitleWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Title = styled.h2`
    margin: 0;
    color: var(--color-Black);
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 0;
`;

const Subtitle = styled.p`
    margin: 0;
    color: var(--color-Grey1);
    font-size: 1.2rem;
    line-height: 1.5;
`;

const CloseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;

    &:hover {
        background: var(--color-Base1);
    }
`;

const Body = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px;
    min-height: 0;
    padding: 18px 20px;
    overflow: auto;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 10px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: var(--color-SubBlack);
    font-size: 1.3rem;
    font-weight: 700;
`;

const SelectedTextBox = styled.div`
    min-height: 180px;
    max-height: 320px;
    padding: 12px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-Base1);
    color: var(--color-Black);
    font-size: 1.3rem;
    line-height: 1.65;
    white-space: pre-wrap;
    overflow: auto;
`;

const PresetGrid = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
`;

const PresetButton = styled.button`
    flex: 0 0 auto;
    height: 30px;
    padding: 0 10px;
    border: 1px solid ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 6px;
    background: ${(props) => (props.$active ? "var(--color-Button4)" : "var(--color-White)")};
    color: ${(props) => (props.$active ? "var(--color-Key)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: var(--color-Key);
    }
`;

const PromptTextarea = styled.textarea`
    width: 100%;
    min-height: 112px;
    padding: 10px 12px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    resize: vertical;
    color: var(--color-Black);
    font-size: 1.3rem;
    line-height: 1.5;
    outline: none;

    &:focus {
        border-color: var(--color-Key);
    }
`;

const ResponseBox = styled.div`
    flex: 1;
    min-height: 360px;
    padding: 12px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    color: var(--color-Black);
    font-size: 1.3rem;
    line-height: 1.65;
    white-space: pre-wrap;
    overflow: auto;
`;

const PreviewBody = styled.div`
    min-height: 0;
    padding: 18px 20px;
    overflow: auto;
`;

const PreviewGuide = styled.div`
    margin-bottom: 12px;
    color: var(--color-SubBlack);
    font-size: 1.3rem;
    font-weight: 600;
`;

const DiffLegend = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 10px;
    color: var(--color-Grey1);
    font-size: 1.2rem;
`;

const DiffLegendItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const DiffLegendSwatch = styled.span`
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${(props) => (props.$type === "remove" ? "#fee2e2" : "#dcfce7")};
`;

const DiffBox = styled.div`
    min-height: 360px;
    padding: 14px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    color: var(--color-Black);
    font-size: 1.4rem;
    line-height: 1.75;
    white-space: pre-wrap;
`;

const DiffSegment = styled.span`
    border-radius: 3px;
    background: ${(props) => {
        if (props.$type === "remove") return "#fee2e2";
        if (props.$type === "add") return "#dcfce7";
        return "transparent";
    }};
    color: ${(props) => (props.$type === "remove" ? "#991b1b" : "inherit")};
    text-decoration: ${(props) => (props.$type === "remove" ? "line-through" : "none")};
`;

const Placeholder = styled.span`
    color: var(--color-Grey1);
`;

const Message = styled.div`
    color: ${(props) => (props.$error ? "var(--color-Red)" : "var(--color-Grey1)")};
    font-size: 1.2rem;
    line-height: 1.5;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 14px 20px;
    border-top: 1px solid var(--color-Line);
`;

const ActionRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const ActionButton = styled.button`
    height: 34px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$primary ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 6px;
    background: ${(props) => (props.$primary ? "var(--color-Key)" : "var(--color-White)")};
    color: ${(props) => (props.$primary ? "var(--color-White)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

function NoteAIEditModal({
    applyMode = "replace",
    errorMessage,
    isStreaming,
    onClose,
    onConfirmApply,
    onPromptChange,
    onApply,
    onPreviewBack,
    onRequest,
    onSelectPreset,
    onStop,
    prompt,
    responseText,
    selectedPresetId,
    selection,
    previewMode,
    status,
}) {
    const displayResponseText = getDisplayAIEditText(responseText);
    const previewNextText =
        applyMode === "append"
            ? [selection?.selectedText || "", displayResponseText].filter(Boolean).join("\n")
            : displayResponseText || "";
    const diffSegments = previewMode ? buildInlineDiffSegments(selection?.selectedText || "", previewNextText) : [];

    return (
        <Backdrop onMouseDown={onClose}>
            <Dialog
                role="dialog"
                aria-modal="true"
                aria-label="AI 편집"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Header>
                    <TitleWrap>
                        <Title>AI 편집</Title>
                        <Subtitle>선택 영역의 텍스트를 문서 맥락에 맞게 편집합니다.</Subtitle>
                    </TitleWrap>
                    <CloseButton type="button" aria-label="닫기" onClick={onClose}>
                        <Icon name="x" size="14px" color="var(--color-SubBlack)" />
                    </CloseButton>
                </Header>
                {previewMode ? (
                    <PreviewBody>
                        <PreviewGuide>아래 변경 사항을 현재 선택 영역에 반영하시겠습니까?</PreviewGuide>
                        <DiffLegend>
                            <DiffLegendItem>
                                <DiffLegendSwatch $type="remove" />
                                삭제될 텍스트
                            </DiffLegendItem>
                            <DiffLegendItem>
                                <DiffLegendSwatch $type="add" />
                                추가될 텍스트
                            </DiffLegendItem>
                        </DiffLegend>
                        <DiffBox>
                            {diffSegments.length
                                ? diffSegments.map((segment, index) => (
                                      <DiffSegment key={`${segment.type}-${index}`} $type={segment.type}>
                                          {segment.value}
                                      </DiffSegment>
                                  ))
                                : "변경할 내용이 없습니다."}
                        </DiffBox>
                    </PreviewBody>
                ) : (
                    <Body>
                        <Section>
                            <SectionTitle>선택 영역</SectionTitle>
                            <SelectedTextBox>{selection?.selectedText || "선택된 텍스트가 없습니다."}</SelectedTextBox>
                            <SectionTitle>요청</SectionTitle>
                            <PresetGrid>
                                {NOTE_AI_EDIT_PRESETS.map((preset) => (
                                    <PresetButton
                                        key={preset.id}
                                        type="button"
                                        $active={selectedPresetId === preset.id}
                                        onClick={() => onSelectPreset?.(preset)}
                                    >
                                        {preset.label}
                                    </PresetButton>
                                ))}
                            </PresetGrid>
                            <PromptTextarea
                                value={prompt}
                                placeholder="AI에게 요청할 내용을 입력하세요."
                                onChange={(event) => onPromptChange?.(event.target.value)}
                            />
                        </Section>
                        <Section>
                            <SectionTitle>AI 편집 결과</SectionTitle>
                            <ResponseBox>
                                {displayResponseText || (
                                    <Placeholder>요청을 시작하면 결과가 여기에 표시됩니다.</Placeholder>
                                )}
                                {isStreaming ? <AiCursor /> : null}
                            </ResponseBox>
                            {errorMessage ? <Message $error>{errorMessage}</Message> : null}
                            {status === "stopped" ? <Message>요청이 중지되었습니다.</Message> : null}
                        </Section>
                    </Body>
                )}
                <Footer>
                    <ActionRow>
                        {previewMode ? (
                            <>
                                <ActionButton type="button" onClick={onPreviewBack}>
                                    뒤로
                                </ActionButton>
                                <ActionButton type="button" onClick={onClose}>
                                    취소
                                </ActionButton>
                                <ActionButton type="button" $primary onClick={onConfirmApply}>
                                    확인
                                </ActionButton>
                            </>
                        ) : (
                            <>
                                <ActionButton type="button" onClick={onStop} disabled={!isStreaming}>
                                    중지
                                </ActionButton>
                                <ActionButton type="button" onClick={onClose}>
                                    취소
                                </ActionButton>
                                <ActionButton
                                    type="button"
                                    $primary
                                    onClick={onRequest}
                                    disabled={isStreaming || !prompt.trim()}
                                >
                                    요청
                                </ActionButton>
                                <ActionButton type="button" onClick={onApply} disabled={isStreaming || !responseText}>
                                    반영
                                </ActionButton>
                            </>
                        )}
                    </ActionRow>
                </Footer>
            </Dialog>
        </Backdrop>
    );
}

export default NoteAIEditModal;
